import { Inject, Injectable } from '@angular/core';
import { SessionService } from '@edl-development/edl-common';
import { firstValueFrom, map } from 'rxjs';
import { ResourceApi } from '../../api/resource.api';
import { ResourceFileTokens } from '../../model/enum/resource/resource-file-tokens.enum';
import { ASSETS_URL } from '../../tokens/api.token';

export interface ResourceData { [key: string]: string };
export interface ResourceFileData { [fileName: string]: any };
export const RESOURCE_FILE_DATA_KEY = 'resource_file_data';

export const DefaultRubricResourceFile: string = "resources/rubric/Rubric.xml";
export const RubricNTPEResourceFilePrefix: string = "resources/ntpe/";
export const RubricNTPEResourceFileSuffix: string = "_NTPE.xml";


@Injectable({
    providedIn: 'root'
})
export class ResourceService {

    constructor(
        @Inject(ASSETS_URL) private assetsUri: string,
        private resourceApi: ResourceApi,
        private sessionService: SessionService,
    ) {}

    getNTPEFilePath(shortName?: string) {
        if (shortName != null && shortName != "Teacher") {
            return RubricNTPEResourceFilePrefix + shortName + RubricNTPEResourceFileSuffix;
        } else {
            return DefaultRubricResourceFile;
        }
    }

    getCachedData(filePath: string = '', key: string = ''): string | undefined {
        const data = this.getCachedResourceData(filePath);
        return data[key];
    }

    getCachedResourceData (filePath: string = ''): ResourceData {
        const filesData: ResourceFileData = this.getFileData();
        return filesData[filePath] ?? {};
    }

    getResourceFile<T = ResourceData>(filePath: string): Promise<T> {
        const fileData: ResourceFileData = this.getFileData();

        if (filePath in fileData) {
            return Promise.resolve(fileData[filePath]);
        }

        return firstValueFrom(this.resourceApi.getResourceFile(filePath)
            .pipe(
                map(data => {
                    const parsedFile =  this.parseResourceFile(filePath, data);
                    this.cacheResourceData(filePath, parsedFile);

                    return parsedFile;
                })
            ));
    }

    private parseResourceFile(filePath: string, data: string): any {
        const fileExtension = filePath.split('.').pop();
        
        switch (fileExtension) {
            case 'json':
                return JSON.parse(data);
            case 'xml':
            default:
                return this.parseResourceFile_XML(data);
        }
    }

    private getFileData(): ResourceFileData {
        return <ResourceFileData>(this.sessionService.GetSessionValue(RESOURCE_FILE_DATA_KEY) ?? {});
    }

    private cacheResourceData(filePath: string, data: any) {
        const fileData = this.getFileData();
        fileData[filePath] = data;

        this.sessionService.SetSessionValue(RESOURCE_FILE_DATA_KEY, fileData);
    }

    private parseResourceFile_XML = (xmlData: string): ResourceData => {
        const parsedFile: { [key: string]: string } = {};
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
        const xmlEntries = xmlDoc.querySelectorAll('data');
        for (let i = 0; i < xmlEntries.length; i++) {
            const xmlEntry = xmlEntries[i];
            const key = xmlEntry.getAttribute('name') as string;
            const value = xmlEntry.querySelector('value')?.textContent as string;
            parsedFile[key] = this.updateTokenData(value) as string;
        }

        return parsedFile;
    }

    private updateTokenData(value?: string) {
        if (!value) {
            return value;
        }

        return value?.replace(this.wrapToken(ResourceFileTokens.ASSETS_URL), this.assetsUri);
    }

    private wrapToken(token: string) {
        return `{${token}}`;
    }
}
